import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LayoutGrid, List, SlidersHorizontal, X, ChevronDown, ChevronLeft, ChevronRight, Tag, Sparkles } from 'lucide-react'
import { productService, categoryService, promotionService } from '../services/api'
import ProductGrid from '../components/product/ProductGrid'
import { Breadcrumbs } from '../components/ui'
import { useAuth } from '../context/AuthContext'

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'name_asc', label: 'Name: A–Z' },
  { value: 'name_desc', label: 'Name: Z–A' },
]

const PRICE_RANGES = [
  { label: 'Any price',             min: null,  max: null   },
  { label: 'Under R1 000',          min: 0,     max: 1000   },
  { label: 'R1 000 – R2 500',       min: 1000,  max: 2500   },
  { label: 'R2 500 – R5 000',       min: 2500,  max: 5000   },
  { label: 'R5 000 – R10 000',      min: 5000,  max: 10000  },
  { label: 'Over R10 000',          min: 10000, max: 999999 },
]

const PAGE_SIZE_OPTIONS = [48, 72, 96, 120, 144]
const DEFAULT_PAGE_SIZE = 48

const UOM_FILTERS = [
  { label: 'Single',   emoji: '1️⃣',  value: 'single',  packsize_min: 1,    packsize_max: 1,    keg: false },
  { label: '4-Pack',   emoji: '📦',  value: '4pack',   packsize_min: 4,    packsize_max: 4,    keg: false },
  { label: '6-Pack',   emoji: '📦',  value: '6pack',   packsize_min: 6,    packsize_max: 6,    keg: false },
  { label: '12-Pack',  emoji: '📦',  value: '12pack',  packsize_min: 12,   packsize_max: 12,   keg: false },
  { label: 'Case',     emoji: '🗃️', value: 'case',    packsize_min: 20,   packsize_max: null, keg: false },
  { label: 'Keg',      emoji: '🪣',  value: 'keg',     packsize_min: null, packsize_max: null, keg: true  },
]

// ── Category banner images — 4 variants per category, randomly selected on navigation ──
// Keys must match the `query` strings used in CATEGORY_GROUPS sidebar entries (lowercased)
const CATEGORY_BANNER_BASE = {
  // ── Beer ─────────────────────────────────────────────────────────────────────
  beer:                            'banner-beer',          // sidebar query: 'beer'
  'beer (mainstream)':             'banner-beer',
  'beer mainstream':               'banner-beer',
  'craft beer':                    'banner-beer-craft',    // sidebar query: 'craft beer'
  'beer - craft':                  'banner-beer-craft',
  'beer — craft':                  'banner-beer-craft',
  'beer craft':                    'banner-beer-craft',
  'non alcoholic beer':            'banner-beer-non-alcoholic', // sidebar query: 'non alcoholic beer'
  'non-alcoholic beer':            'banner-beer-non-alcoholic',

  // ── Wine ─────────────────────────────────────────────────────────────────────
  wine:                            'banner-wine',          // sidebar query: 'wine'
  champagne:                       'banner-champagne-sparkling', // sidebar query: 'champagne'
  'champagne & sparkling wine':    'banner-champagne-sparkling',
  'champagne & sparkling':         'banner-champagne-sparkling',
  sparkling:                       'banner-champagne-sparkling',
  'sparkling wine':                'banner-champagne-sparkling',
  'non alcoholic wine':            'banner-wine-non-alcoholic',  // sidebar query: 'non alcoholic wine'
  'non-alcoholic wine':            'banner-wine-non-alcoholic',
  'port sherry':                   'banner-port-sherry-muscadel', // sidebar query: 'port sherry'
  'port sherry muscadel etc':      'banner-port-sherry-muscadel',
  'port / sherry / muscadel':      'banner-port-sherry-muscadel',
  port:                            'banner-port-sherry-muscadel',
  sherry:                          'banner-port-sherry-muscadel',
  muscadel:                        'banner-port-sherry-muscadel',
  'box wine':                      'banner-wine',          // sidebar query: 'box wine'

  // ── Spirits ───────────────────────────────────────────────────────────────────
  gin:                             'banner-gin',           // sidebar query: 'gin'
  vodka:                           'banner-vodka',         // sidebar query: 'vodka'
  whiskey:                         'banner-whisky',        // sidebar query: 'whiskey'
  whisky:                          'banner-whisky',
  rum:                             'banner-rum',           // sidebar query: 'rum'
  tequila:                         'banner-tequila',       // sidebar query: 'tequila'
  brandy:                          'banner-brandy',        // sidebar query: 'brandy'
  liqueur:                         'banner-liqueur',       // sidebar query: 'liqueur'
  liqueurs:                        'banner-liqueur',
  aperitif:                        'banner-aperitifs',     // sidebar query: 'aperitif'
  aperitifs:                       'banner-aperitifs',
  aperitiff:                       'banner-aperitifs',
  aperitiffs:                      'banner-aperitifs',
  cane:                            'banner-cane',          // sidebar query: 'cane'
  'cane spirit':                   'banner-cane',
  shooters:                        'banner-shooters',      // sidebar query: 'shooters'
  spirits:                         'banner-spirits-other', // sidebar query: 'spirits'
  'other spirits':                 'banner-spirits-other',
  'non alcoholic spirits':         'banner-spirits-other',
  'non-alcoholic spirits':         'banner-spirits-other',

  // ── Ciders / RTDs ────────────────────────────────────────────────────────────
  cider:                           'banner-ciders-rtds',   // sidebar query: 'cider'
  'ciders & rtds':                 'banner-ciders-rtds',
  'ciders / r t ds':               'banner-ciders-rtds',
  'ciders / rtds':                 'banner-ciders-rtds',
  ciders:                          'banner-ciders-rtds',
  rtds:                            'banner-ciders-rtds',
  'non alcoholic rtd':             'banner-rtd-non-alcoholic', // sidebar query: 'non alcoholic rtd'
  'non-alcoholic rtd':             'banner-rtd-non-alcoholic',
  'rtd cocktail':                  'banner-rtd-cocktails', // sidebar query: 'rtd cocktail'
  'rtd cocktails':                 'banner-rtd-cocktails',
  'r t d cocktails':               'banner-rtd-cocktails',

  // ── Non-Alcoholic ────────────────────────────────────────────────────────────
  minerals:                        'banner-minerals',      // sidebar query: 'minerals'
  water:                           'banner-water',         // sidebar query: 'water'
  'mineral water':                 'banner-water',
  'mineral drinks':                'banner-minerals',      // sidebar query: 'mineral drinks'
  juice:                           'banner-juice',         // sidebar query: 'juice'
  'fruit juice':                   'banner-juice',
  energy:                          'banner-softdrinks',    // sidebar query: 'energy'
  'energy drinks':                 'banner-softdrinks',
  'energy drink':                  'banner-softdrinks',
  cordials:                        'banner-cordials',      // sidebar query: 'cordials'
  cordial:                         'banner-cordials',
  'soft drinks':                   'banner-softdrinks',
  softdrinks:                      'banner-softdrinks',

  // ── Tobacco ───────────────────────────────────────────────────────────────────
  cigarettes:                      'banner-cigarettes-tobacco', // sidebar query: 'cigarettes'
  tobacco:                         'banner-cigarettes-tobacco',

  // ── Accessories & Misc ────────────────────────────────────────────────────────
  glassware:                       'banner-glassware',     // sidebar query: 'glassware'
  gifts:                           'banner-gifts',         // sidebar query: 'gifts'
  snacks:                          'banner-snacks',        // sidebar query: 'snacks'
}

function getRandomBanner(category) {
  if (!category) {
    const i = Math.floor(Math.random() * 4)
    return `/images/banners/banner-hero-${i}.webp`
  }
  const base = CATEGORY_BANNER_BASE[category.toLowerCase()]
  if (!base) {
    const i = Math.floor(Math.random() * 4)
    return `/images/banners/banner-hero-${i}.webp`
  }
  const i = Math.floor(Math.random() * 4)
  return `/images/banners/${base}-${i}.webp`
}

// ── Parallax scroll-lock constants ───────────────────────────────────────────
const BANNER_H = 460
const IMG_EXTRA = 200

// ── Category taxonomy ─────────────────────────────────────────────────────────
const CATEGORY_GROUPS = [
  {
    label: 'Alcoholic Beverages', emoji: '🍾',
    gradient: 'from-teal-900/90 to-teal-700/70', accent: 'border-teal-500/40',
    sub: [
      { label: 'Beer (Mainstream)', emoji: '🍺', query: 'beer' },
      { label: 'Beer — Craft', emoji: '🍻', query: 'craft beer' },
      { label: 'Non-Alcoholic Beer', emoji: '🥛', query: 'non alcoholic beer' },
      { label: 'Wine', emoji: '🍷', query: 'wine' },
      { label: 'Champagne & Sparkling', emoji: '🥂', query: 'champagne' },
      { label: 'Non-Alcoholic Wine', emoji: '🍇', query: 'non alcoholic wine' },
      { label: 'Port, Sherry & Muscadel', emoji: '🫙', query: 'port sherry' },
      { label: 'Box Wine', emoji: '📦', query: 'box wine' },
      { label: 'Gin', emoji: '🌿', query: 'gin' },
      { label: 'Vodka', emoji: '🍸', query: 'vodka' },
      { label: 'Whiskey', emoji: '🥃', query: 'whiskey' },
      { label: 'Rum', emoji: '🏝️', query: 'rum' },
      { label: 'Tequila', emoji: '🌵', query: 'tequila' },
      { label: 'Brandy', emoji: '🍂', query: 'brandy' },
      { label: 'Liqueur', emoji: '✨', query: 'liqueur' },
      { label: 'Aperitifs', emoji: '🫗', query: 'aperitif' },
      { label: 'Cane', emoji: '🌾', query: 'cane' },
      { label: 'Shooters', emoji: '🔫', query: 'shooters' },
      { label: 'Other Spirits', emoji: '🥄', query: 'spirits' },
      { label: 'Ciders & RTDs', emoji: '🍋', query: 'cider' },
      { label: 'Non-Alcoholic RTD', emoji: '🧴', query: 'non alcoholic rtd' },
      { label: 'RTD Cocktails', emoji: '🍹', query: 'rtd cocktail' },
    ],
  },
  {
    label: 'Non-Alcoholic', emoji: '🥤',
    gradient: 'from-blue-900/90 to-cyan-800/70', accent: 'border-blue-500/40',
    sub: [
      { label: 'Minerals', emoji: '💧', query: 'minerals' },
      { label: 'Mineral Water', emoji: '🫧', query: 'water' },
      { label: 'Mineral Drinks', emoji: '🧃', query: 'mineral drinks' },
      { label: 'Juice', emoji: '🍊', query: 'juice' },
      { label: 'Energy Drinks', emoji: '⚡', query: 'energy' },
      { label: 'Cordials', emoji: '🍓', query: 'cordials' },
    ],
  },
  {
    label: 'Tobacco', emoji: '🚬',
    gradient: 'from-gray-800/90 to-gray-700/70', accent: 'border-gray-500/40',
    sub: [{ label: 'Cigarettes', emoji: '🚬', query: 'cigarettes' }],
  },
  {
    label: 'Accessories & More', emoji: '🎁',
    gradient: 'from-yellow-900/70 to-amber-800/60', accent: 'border-gold/40',
    sub: [
      { label: 'Glassware', emoji: '🥂', query: 'glassware' },
      { label: 'Gifts', emoji: '🎁', query: 'gifts' },
      { label: 'Snacks', emoji: '🍿', query: 'snacks' },
    ],
  },
]

// ── Parallax banner ───────────────────────────────────────────────────────────
// Approach: fixed-height container with overflow:hidden. The image is taller
// than the container by IMG_EXTRA px. As the user scrolls, the image translates
// upward revealing the hidden portion — pure parallax, no sticky, no z-index fights.
function ShopBanner({ bannerUrl, activeCategory, total, isLoading }) {
  const wrapperRef = useRef(null)
  const rafRef = useRef(null)
  const [imgOffset, setImgOffset] = useState(0)

  const onScroll = useCallback(() => {
    if (!wrapperRef.current) return
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      if (!wrapperRef.current) return
      const rect = wrapperRef.current.getBoundingClientRect()
      // How far the banner top has scrolled above the viewport top
      const scrolledPast = Math.max(0, -rect.top)
      // Translate image up by half the scroll distance (parallax ratio 0.5)
      const offset = Math.min(scrolledPast * 0.5, IMG_EXTRA)
      setImgOffset(offset)
    })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [onScroll])

  return (
    <div
      ref={wrapperRef}
      className="relative w-full rounded-2xl overflow-hidden mb-6"
      style={{ height: `${BANNER_H}px` }}
    >
      <img
        key={bannerUrl}
        src={bannerUrl}
        alt={activeCategory || 'Shop'}
        className="absolute inset-x-0 top-0 w-full object-cover transition-opacity duration-500"
        style={{
          height: `${BANNER_H + IMG_EXTRA}px`,
          objectPosition: 'center 30%',
          transform: `translateY(-${imgOffset}px)`,
          willChange: 'transform',
        }}
        onError={e => {
          // Only try the fallback once — if it also fails, stop (don't loop)
          if (e.target.dataset.fallbackTried) return
          e.target.dataset.fallbackTried = 'true'
          e.target.src = '/images/banners/banner-hero-0.webp'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

      {/* Gold progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
        <div className="h-full bg-gold transition-none" style={{ width: `${(imgOffset / IMG_EXTRA) * 100}%` }} />
      </div>

      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <h1 className="text-2xl md:text-4xl font-heading text-white drop-shadow-lg">
          {activeCategory
            ? activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)
            : 'All Products'}
        </h1>
        {!isLoading && (
          <p className="text-sm text-white/70 mt-1">{total} product{total !== 1 ? 's' : ''} found</p>
        )}
        <div
          className="absolute bottom-6 right-6 flex flex-col items-center gap-1 transition-opacity duration-300"
          style={{ opacity: imgOffset > 10 ? 0 : 0.5 }}
        >
          <div className="w-px h-5 bg-white/40 animate-pulse" />
        </div>
      </div>
    </div>
  )
}

// ── Category browser ──────────────────────────────────────────────────────────
// ── Shop Category Browser — 4-panel tiles matching Home page style ────────────
function ShopCategoryBrowser({ onSelect, canViewAlcohol }) {
  const [open, setOpen] = useState(null)

  const visibleGroups = CATEGORY_GROUPS
    .filter(g => {
      if (!canViewAlcohol && g.label === 'Tobacco') return false
      if (!canViewAlcohol && g.label === 'Alcoholic Beverages') return false
      return true
    })
    .map(g => {
      if (!canViewAlcohol && g.label === 'Accessories & More')
        return { ...g, sub: g.sub.filter(s => s.query !== 'gifts') }
      return g
    })

  return (
    <div className="space-y-3 mb-6">
      {/* 4 top-level group tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {visibleGroups.map((group, i) => {
          const isOpen = open === i
          return (
            <button
              key={group.label}
              onClick={() => setOpen(isOpen ? null : i)}
              className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-300 text-left
                ${isOpen
                  ? `${group.accent} scale-[1.02] shadow-lg shadow-black/30`
                  : 'border-transparent hover:border-white/10'
                }`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${group.gradient}`} />
              <div className="absolute inset-0 bg-black/20" />
              <div className="relative p-4 flex items-center gap-3">
                <span className="text-2xl leading-none flex-shrink-0">{group.emoji}</span>
                <div className="min-w-0">
                  <div className="font-display font-bold text-white text-xs leading-tight truncate">{group.label}</div>
                  <div className="text-white/60 text-[10px] mt-0.5">{group.sub.length} categories</div>
                </div>
                <div className={`ml-auto transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
                  <svg width="10" height="10" viewBox="0 0 10 10" className="text-white/50 fill-current">
                    <path d="M5 7L1 3h8z"/>
                  </svg>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Expanded sub-category chip panel */}
      {open !== null && open < visibleGroups.length && (
        <div className={`rounded-2xl border bg-gray-50 dark:bg-gray-900/80 backdrop-blur-sm p-4 animate-fade-in ${visibleGroups[open].accent}`}>
          <p className="text-xs font-display font-semibold text-gray-400 uppercase tracking-widest mb-3">
            {visibleGroups[open].label}
          </p>
          <div className="flex flex-wrap gap-2">
            {visibleGroups[open].sub.map(sub => (
              <button
                key={sub.query}
                onClick={() => { onSelect(sub.query); setOpen(null) }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-sm font-display text-gray-700 dark:text-gray-200 hover:text-primary"
              >
                <span>{sub.emoji}</span>
                <span>{sub.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Promotions carousel ───────────────────────────────────────────────────────
function PromotionsSection({ onNavigate }) {
  const { data: promos = [] } = useQuery({
    queryKey: ['shop-promotions'],
    queryFn: promotionService.getMyPromos,
    staleTime: 1000 * 60 * 5,
  })

  const [idx, setIdx] = useState(0)
  const active = promos.filter(p => !p.dismissed)

  useEffect(() => {
    if (active.length <= 1) return
    const t = setInterval(() => setIdx(i => (i + 1) % active.length), 5000)
    return () => clearInterval(t)
  }, [active.length])

  if (!active.length) return null

  const promo = active[idx]

  const discountLabel = () => {
    if (promo.discount_type === 'percentage' && promo.discount_value)
      return `${promo.discount_value}% OFF`
    if (promo.discount_type === 'fixed' && promo.discount_value)
      return `R${promo.discount_value} OFF`
    if (promo.discount_type === 'free_shipping')
      return 'FREE SHIPPING'
    return 'SPECIAL OFFER'
  }

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-gold" />
        <h2 className="font-heading text-base text-gray-800 dark:text-white uppercase tracking-wide">
          Current Promotions
        </h2>
        {active.length > 1 && (
          <span className="ml-auto text-xs text-gray-400 font-display">
            {idx + 1} / {active.length}
          </span>
        )}
      </div>

      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-primary via-red-800 to-primary/80 shadow-lg shadow-primary/20">
        {/* Background banner image if available */}
        {promo.banner_image && (
          <img
            src={promo.banner_image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
        )}

        <div className="relative flex flex-col md:flex-row items-center gap-4 p-5 md:p-6">
          {/* Discount badge */}
          <div className="flex-shrink-0 w-20 h-20 rounded-full bg-gold flex flex-col items-center justify-center shadow-lg shadow-gold/30">
            <Tag size={14} className="text-black mb-0.5" />
            <span className="font-heading text-black text-sm leading-tight text-center px-1">
              {discountLabel()}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading text-white text-lg md:text-xl leading-tight mb-1">
              {promo.title}
            </h3>
            {promo.description && (
              <p className="text-white/80 text-sm leading-relaxed line-clamp-2">
                {promo.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-3 justify-center md:justify-start">
              {promo.min_order_value && (
                <span className="text-white/60 text-xs font-display">
                  Min. order R{promo.min_order_value}
                </span>
              )}
              {promo.ends_at && (
                <span className="text-white/60 text-xs font-display">
                  Ends {new Date(promo.ends_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>
          </div>

          {/* CTA */}
          {promo.cta_label && promo.cta_link && (
            <div className="flex-shrink-0">
              <button
                onClick={() => onNavigate(promo.cta_link)}
                className="px-5 py-2.5 rounded-xl bg-gold text-black font-display font-bold text-sm hover:bg-yellow-400 transition-colors shadow-md shadow-gold/30"
              >
                {promo.cta_label}
              </button>
            </div>
          )}
        </div>

        {/* Dot indicators */}
        {active.length > 1 && (
          <div className="absolute bottom-3 right-4 flex gap-1.5">
            {active.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? 'bg-gold w-4' : 'bg-white/30'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Numbered pagination ───────────────────────────────────────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null

  const getPages = () => {
    const pages = []
    const delta = 2
    const left = Math.max(1, page - delta)
    const right = Math.min(totalPages, page + delta)

    if (left > 1) {
      pages.push(1)
      if (left > 2) pages.push('...')
    }
    for (let i = left; i <= right; i++) pages.push(i)
    if (right < totalPages) {
      if (right < totalPages - 1) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1.5 mt-10 flex-wrap">
      {/* Prev */}
      <button
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-display text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeft size={14} /> Prev
      </button>

      {/* Page numbers */}
      {getPages().map((p, i) =>
        p === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 py-2 text-gray-400 text-sm select-none">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`w-9 h-9 rounded-lg text-sm font-display font-semibold transition-all ${
              p === page
                ? 'bg-primary text-white shadow-sm shadow-primary/30'
                : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary'
            }`}
          >
            {p}
          </button>
        )
      )}

      {/* Next */}
      <button
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-display text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      >
        Next <ChevronRight size={14} />
      </button>

      {/* Page info */}
      <span className="w-full text-center text-xs text-gray-400 font-display mt-1">
        Page {page} of {totalPages}
      </span>
    </div>
  )
}

// ── Related products strip ────────────────────────────────────────────────────
function RelatedProducts({ category, currentProducts, onNavigate }) {
  const { data, isLoading } = useQuery({
    queryKey: ['related-products', category],
    queryFn: () => productService.getAll({
      category: category || undefined,
      limit: 12,
      sort: 'newest',
    }),
    enabled: !!category,
    staleTime: 1000 * 60 * 5,
  })

  const currentIds = new Set((currentProducts || []).map(p => p.id))
  const related = (data?.items || data || []).filter(p => !currentIds.has(p.id)).slice(0, 8)

  if (!category || isLoading || related.length === 0) return null

  return (
    <div className="mt-14 border-t border-gray-200 dark:border-gray-700 pt-10">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-primary rounded-full" />
          <h2 className="font-heading text-xl text-gray-800 dark:text-white">
            More in {category.charAt(0).toUpperCase() + category.slice(1)}
          </h2>
        </div>
        <button
          onClick={() => onNavigate(`/shop?category=${encodeURIComponent(category)}`)}
          className="text-sm text-primary hover:underline font-display flex items-center gap-1"
        >
          View all <ChevronRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3">
        {related.map(product => (
          <RelatedProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  )
}

function RelatedProductCard({ product }) {
  const price = product.inclprice ?? product.selling_price ?? product.price ?? 0
  const name  = product.description || product.name || ''
  const imgSrc = product.primary_image
    ? `/MappedData/${product.primary_image}`
    : product.image_url
      ? product.image_url.startsWith('http') ? product.image_url : `/MappedData/${product.image_url}`
      : null

  // Category-appropriate fallback emoji (no image case)
  const cat = (product.category_name || product.category || '').toLowerCase()
  const combined = (product.name || product.description || '').toLowerCase()
  const fallbackEmoji = (() => {
    if (/whiskey|whisky|bourbon|scotch/.test(cat)) return '🥃'
    if (/vodka/.test(cat))       return '🍸'
    if (/\bgin\b/.test(cat))     return '🌿'
    if (/tequila/.test(cat))     return '🌵'
    if (/rum/.test(cat))         return '🏝️'
    if (/brandy/.test(cat))      return '🍂'
    if (/liqueur/.test(cat))     return '✨'
    if (/aperitif/.test(cat))    return '🫗'
    if (/cane/.test(cat))        return '🌾'
    if (/shooter/.test(cat))     return '🔫'
    if (/champagne|sparkling/.test(cat)) return '🥂'
    if (/port|sherry|muscadel/.test(cat)) return '🫙'
    if (/box wine/.test(cat))    return '📦'
    if (/wine/.test(cat))        return '🍷'
    if (/craft beer/.test(cat))  return '🍻'
    if (/cider/.test(cat))       return '🍋'
    if (/rtd cocktail/.test(cat)) return '🍹'
    if (/rtd/.test(cat))         return '🥤'
    if (/beer/.test(cat))        return '🍺'
    if (/energy/.test(cat))      return '⚡'
    if (/juice/.test(cat))       return '🍊'
    if (/cordial/.test(cat))     return '🍓'
    if (/water/.test(cat))       return '🫧'
    if (/mineral/.test(cat))     return '💧'
    if (/cigarette|tobacco/.test(cat)) return '🚬'
    if (/spirits/.test(cat))     return '🥃'
    if (/snack|chip|crisp|nut|biltong/.test(cat)) return '🍿'
    if (/glassware|glass/.test(cat)) return '🥂'
    if (/gift|hamper/.test(cat)) return '🎁'
    if (/willards|simba|lays|pringles|snack|chip/.test(combined)) return '🍿'
    return '🍾'
  })()

  return (
    <Link
      to={`/product/${product.id}`}
      className="group flex flex-col rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:shadow-md hover:shadow-primary/10 transition-all bg-white dark:bg-gray-900"
    >
      <div className="aspect-square bg-gray-100 dark:bg-gray-800 overflow-hidden">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={name}
            className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600 text-2xl">
            {fallbackEmoji}
          </div>
        )}
      </div>
      <div className="p-2 flex-1 flex flex-col gap-0.5">
        <p className="text-xs font-display text-gray-700 dark:text-gray-200 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
          {name}
        </p>
        <p className="text-xs font-heading text-primary mt-auto pt-1">
          R{Number(price).toFixed(2)}
        </p>
      </div>
    </Link>
  )
}

// ── Main Shop page ────────────────────────────────────────────────────────────
export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { canViewAlcohol } = useAuth()
  const [view, setView] = useState('grid')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const searchQ    = searchParams.get('search') || ''
  const categoryQ  = searchParams.get('category') || ''
  const sortQ      = searchParams.get('sort') || 'newest'
  const priceMin   = searchParams.get('price_min')
  const priceMax   = searchParams.get('price_max')
  const brandQ     = searchParams.get('brand') || ''
  const pageParam  = parseInt(searchParams.get('page') || '1', 10)
  const page       = isNaN(pageParam) || pageParam < 1 ? 1 : pageParam
  const pageSizeParam = parseInt(searchParams.get('per_page') || String(DEFAULT_PAGE_SIZE), 10)
  const pageSize   = PAGE_SIZE_OPTIONS.includes(pageSizeParam) ? pageSizeParam : DEFAULT_PAGE_SIZE
  const uomFilter  = searchParams.get('uom') || ''
  const activeCategory = categoryQ || null

  const [bannerUrl, setBannerUrl] = useState(() => getRandomBanner(activeCategory))

  useEffect(() => {
    setBannerUrl(getRandomBanner(activeCategory))
  }, [activeCategory])

  const topRef = useRef(null)

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value); else next.delete(key)
    next.delete('page')
    setSearchParams(next)
    if (key === 'category') {
      setBannerUrl(getRandomBanner(value || null))
      // Scroll to product grid (not page top) — avoids parallax banner hijack
      setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    }
  }

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams)
    if (p > 1) next.set('page', String(p)); else next.delete('page')
    setSearchParams(next)
    // Scroll to product grid top
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
  }

  const clearFilters = () => {
    setSearchParams({})
    setBannerUrl(getRandomBanner(null))
  }

  const hasFilters = searchQ || categoryQ || priceMin || priceMax || brandQ || uomFilter

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoryService.getAll,
    staleTime: 1000 * 60 * 10,
  })

  const { data: brandsData } = useQuery({
    queryKey: ['product-brands'],
    queryFn: productService.getBrands,
    staleTime: 1000 * 60 * 10,
  })

  // Resolve packsize params from active UOM filter
  const activeUom = UOM_FILTERS.find(u => u.value === uomFilter)
  const packsizeMin = activeUom?.packsize_min ?? undefined
  const packsizeMax = activeUom?.packsize_max ?? undefined
  const isKeg      = activeUom?.keg ?? false

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', { searchQ, categoryQ, sortQ, priceMin, priceMax, brandQ, page, pageSize, uomFilter }],
    queryFn: () => productService.getAll({
      search:        searchQ || undefined,
      category:      categoryQ || undefined,
      sort:          sortQ,
      price_min:     priceMin || undefined,
      price_max:     priceMax || undefined,
      brand:         brandQ || undefined,
      page,
      limit:         pageSize,
      packsize_min:  packsizeMin,
      packsize_max:  packsizeMax,
      keg:           isKeg || undefined,
    }),
    staleTime: 1000 * 60 * 2,
    keepPreviousData: true,
  })

  const products   = data?.items || data || []
  const total      = data?.total || products.length
  const totalPages = Math.ceil(total / pageSize)

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 animate-fade-in">
      {/* Parallax banner */}
      <ShopBanner
        bannerUrl={bannerUrl}
        activeCategory={searchQ ? null : activeCategory}
        total={total}
        isLoading={isLoading}
      />

      <Breadcrumbs items={[
        { href: '/', label: 'Home' },
        { label: activeCategory ? activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1) : 'Shop' },
      ]} />

      {/* Promotions — shown when browsing, not when searching */}
      {!searchQ && <PromotionsSection onNavigate={navigate} />}

      {/* Category browser */}
      {!searchQ && (
        <ShopCategoryBrowser onSelect={q => updateParam('category', q)} canViewAlcohol={canViewAlcohol} />
      )}

      <div className="flex items-center justify-between mb-6" ref={topRef}>
        {/* Product count */}
        <p className="text-sm text-gray-500 dark:text-gray-400 font-display">
          {isLoading ? (
            <span className="animate-pulse">Loading…</span>
          ) : (
            <>
              <span className="font-semibold text-gray-700 dark:text-gray-200">{total.toLocaleString()}</span>
              {' '}product{total !== 1 ? 's' : ''}
              {activeCategory && (
                <span className="text-gray-400"> in <span className="text-primary font-medium capitalize">{activeCategory}</span></span>
              )}
              {totalPages > 1 && (
                <span className="text-gray-400"> · page {page}/{totalPages}</span>
              )}
            </>
          )}
        </p>
        <div className="flex items-center gap-2 flex-wrap justify-end">

          {/* UOM filter pills */}
          <div className="flex items-center gap-1 flex-wrap">
            {UOM_FILTERS.map(u => (
              <button
                key={u.value}
                onClick={() => updateParam('uom', uomFilter === u.value ? '' : u.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-display font-medium border transition-colors ${
                  uomFilter === u.value
                    ? 'bg-primary text-white border-primary'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary'
                }`}
              >
                {u.emoji} {u.label}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortQ}
              onChange={e => updateParam('sort', e.target.value)}
              className="appearance-none input-field pr-8 text-sm py-2 cursor-pointer"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* Per-page selector */}
          <div className="relative">
            <select
              value={pageSize}
              onChange={e => updateParam('per_page', e.target.value)}
              className="appearance-none input-field pr-8 text-sm py-2 cursor-pointer"
              title="Products per page"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>Show {n}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <div className="flex border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('grid')}
              className={`p-2.5 transition-colors ${view === 'grid' ? 'bg-primary text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setView('list')}
              className={`p-2.5 transition-colors ${view === 'list' ? 'bg-primary text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <List size={16} />
            </button>
          </div>

          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden flex items-center gap-2 btn-outline py-2 text-sm"
          >
            <SlidersHorizontal size={15} />
            Filters
            {hasFilters && <span className="w-2 h-2 bg-primary rounded-full" />}
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - desktop */}
        <aside className="hidden lg:block w-60 flex-shrink-0 sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain rounded-xl sidebar-scroll pr-1">
          <FilterPanel
            categories={categoriesData || []}
            brands={brandsData || []}
            activeCategory={categoryQ}
            activeBrand={brandQ}
            onCategory={v => updateParam('category', v)}
            onBrand={v => updateParam('brand', v)}
            priceMin={priceMin}
            priceMax={priceMax}
            onPriceRange={(min, max) => {
              updateParam('price_min', min)
              updateParam('price_max', max)
            }}
            onClear={clearFilters}
            hasFilters={hasFilters}
            canViewAlcohol={canViewAlcohol}
          />
        </aside>

        {/* Mobile sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white dark:bg-gray-900 overflow-y-auto p-4 animate-slide-up">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg">Filters</h3>
                <button onClick={() => setSidebarOpen(false)}><X size={20} /></button>
              </div>
              <FilterPanel
                categories={categoriesData || []}
                brands={brandsData || []}
                activeCategory={categoryQ}
                activeBrand={brandQ}
                onCategory={v => { updateParam('category', v); setSidebarOpen(false) }}
                onBrand={v => { updateParam('brand', v); setSidebarOpen(false) }}
                priceMin={priceMin}
                priceMax={priceMax}
                onPriceRange={(min, max) => { updateParam('price_min', min); updateParam('price_max', max) }}
                onClear={clearFilters}
                hasFilters={hasFilters}
                canViewAlcohol={canViewAlcohol}
              />
            </div>
          </div>
        )}

        {/* Products column */}
        <div className="flex-1 min-w-0">
          {hasFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {searchQ && <FilterChip label={`"${searchQ}"`} onRemove={() => updateParam('search', '')} />}
              {categoryQ && <FilterChip label={categoryQ} onRemove={() => updateParam('category', '')} />}
              {brandQ && <FilterChip label={`Brand: ${brandQ}`} onRemove={() => updateParam('brand', '')} />}
              {priceMin && priceMax && <FilterChip label={`R${priceMin}–R${priceMax}`} onRemove={() => { updateParam('price_min', ''); updateParam('price_max', '') }} />}
              <button onClick={clearFilters} className="text-xs text-primary hover:underline font-display">
                Clear all
              </button>
            </div>
          )}

          <ProductGrid
            products={products}
            loading={isLoading}
            view={view}
            emptyMessage={hasFilters ? 'No products match your filters.' : 'No products available.'}
          />

          {/* Numbered pagination */}
          <Pagination page={page} totalPages={totalPages} onPage={setPage} />

          {/* Related products */}
          <RelatedProducts
            category={activeCategory}
            currentProducts={products}
            onNavigate={navigate}
          />
        </div>
      </div>
    </div>
  )
}

// ── Filter panel ──────────────────────────────────────────────────────────────
function FilterPanel({ categories, brands, activeCategory, activeBrand, onCategory, onBrand, priceMin, priceMax, onPriceRange, onClear, hasFilters, canViewAlcohol }) {
  const [brandSearch, setBrandSearch] = useState('')

  // Lookup tables — defined here so useEffect can use them
  const groupVals = [
    { key: 'alcoholic', vals: ['beer mainstream','craft beer','non alcoholic beer','wine','champagne','non alcoholic wine','port sherry','box wine','gin','vodka','whiskey','rum','tequila','brandy','liqueur','aperitif','cane','shooters','spirits','cider','non alcoholic rtd','rtd cocktail'] },
    { key: 'nonalcoholic', vals: ['minerals','water','mineral drinks','juice','energy','cordials','non alcoholic beer','non alcoholic wine','non alcoholic rtd','soft drinks'] },
    { key: 'tobacco', vals: ['cigarettes'] },
    { key: 'accessories', vals: ['glassware','gifts','snacks'] },
  ]
  const beerVals    = ['beer mainstream','craft beer','non alcoholic beer']
  const wineVals    = ['wine','champagne','non alcoholic wine','port sherry','box wine']
  const spiritsVals = ['gin','vodka','whiskey','rum','tequila','brandy','liqueur','aperitif','cane','shooters','spirits']
  const rtdVals     = ['cider','non alcoholic rtd','rtd cocktail']

  const getGroupKey = (cat) => groupVals.find(g => g.vals.includes(cat))?.key || 'alcoholic'
  const getSubKey   = (cat) => {
    if (!cat) return 'beer'
    if (beerVals.includes(cat))    return 'beer'
    if (wineVals.includes(cat))    return 'wine'
    if (spiritsVals.includes(cat)) return 'spirits'
    if (rtdVals.includes(cat))     return 'rtd'
    return ''
  }

  const [catGroupOpen, setCatGroupOpen] = useState(() => activeCategory ? getGroupKey(activeCategory) : 'alcoholic')
  const [subGroupOpen, setSubGroupOpen] = useState(() => getSubKey(activeCategory))

  // Sync sidebar open state when header browser changes the active category
  useEffect(() => {
    if (activeCategory) {
      setCatGroupOpen(getGroupKey(activeCategory))
      setSubGroupOpen(getSubKey(activeCategory))
    }
  }, [activeCategory])

  const CAT_GROUPS = [
    {
      key: 'alcoholic',
      label: '🍾 Alcoholic Beverages',
      // Sub-groups inside Alcoholic
      subGroups: [
        {
          key: 'beer',
          label: '🍺 Beer',
          cats: [
            { label: '🍺 Mainstream Beer',    value: 'beer mainstream' },
            { label: '🍻 Craft Beer',          value: 'craft beer' },
            { label: '🥤 Non-Alcoholic Beer',  value: 'non alcoholic beer' },
          ],
        },
        {
          key: 'wine',
          label: '🍷 Wine',
          cats: [
            { label: '🍷 Wine',                       value: 'wine' },
            { label: '🥂 Champagne & Sparkling',      value: 'champagne' },
            { label: '🫙 Port / Sherry / Muscadel',   value: 'port sherry' },
            { label: '📦 Box Wine',                   value: 'box wine' },
            { label: '🍷 Non-Alcoholic Wine',         value: 'non alcoholic wine' },
          ],
        },
        {
          key: 'spirits',
          label: '🥃 Spirits',
          cats: [
            { label: '🌿 Gin',           value: 'gin' },
            { label: '🍸 Vodka',         value: 'vodka' },
            { label: '🥃 Whiskey',       value: 'whiskey' },
            { label: '🏝️ Rum',           value: 'rum' },
            { label: '🌵 Tequila',       value: 'tequila' },
            { label: '🍂 Brandy',        value: 'brandy' },
            { label: '✨ Liqueur',        value: 'liqueur' },
            { label: '🫗 Aperitifs',     value: 'aperitif' },
            { label: '🌾 Cane',          value: 'cane' },
            { label: '🔫 Shooters',      value: 'shooters' },
            { label: '🥃 Other Spirits', value: 'spirits' },
          ],
        },
        {
          key: 'rtd',
          label: '🍹 Ciders & RTDs',
          cats: [
            { label: '🍋 Ciders',                value: 'cider' },
            { label: '🍹 RTD Cocktails',          value: 'rtd cocktail' },
            { label: '🥤 Non-Alcoholic RTD',      value: 'non alcoholic rtd' },
          ],
        },
      ],
    },
    {
      key: 'nonalcoholic',
      label: '🥤 Non-Alcoholic',
      cats: [
        { label: '💧 Minerals',            value: 'minerals' },
        { label: '🫧 Mineral Water',       value: 'water' },
        { label: '💧 Mineral Drinks',      value: 'mineral drinks' },
        { label: '🍊 Juice',               value: 'juice' },
        { label: '⚡ Energy Drinks',        value: 'energy' },
        { label: '🍓 Cordials',            value: 'cordials' },
        { label: '🥤 Non-Alcoholic Beer',  value: 'non alcoholic beer' },
        { label: '🍷 Non-Alcoholic Wine',  value: 'non alcoholic wine' },
        { label: '🥤 Non-Alcoholic RTD',   value: 'non alcoholic rtd' },
      ],
    },
    {
      key: 'tobacco',
      label: '🚬 Tobacco',
      cats: [{ label: '🚬 Cigarettes', value: 'cigarettes' }],
    },
    {
      key: 'accessories',
      label: '🎁 Accessories & Misc',
      cats: [
        { label: '🥂 Glassware', value: 'glassware' },
        { label: '🎁 Gifts',     value: 'gifts' },
        { label: '🍿 Snacks',    value: 'snacks' },
      ],
    },
  ]

  const filteredBrands = brands.filter(b =>
    b.toLowerCase().includes(brandSearch.toLowerCase())
  ).slice(0, 20)

  // Apply alcohol permission filtering to sidebar groups
  const visibleCatGroups = CAT_GROUPS
    .filter(g => {
      if (!canViewAlcohol && g.key === 'alcoholic') return false
      if (!canViewAlcohol && g.key === 'tobacco') return false
      return true
    })
    .map(g => {
      if (!canViewAlcohol && g.key === 'accessories') {
        return { ...g, cats: (g.cats || []).filter(c => c.value !== 'gifts') }
      }
      return g
    })

  return (
    <div className="space-y-6">
      {hasFilters && (
        <button onClick={onClear} className="text-sm text-primary hover:underline font-display flex items-center gap-1">
          <X size={13} /> Clear all filters
        </button>
      )}

      <div>
        <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-3 text-sm uppercase tracking-wide">
          Categories
        </h4>
        <ul className="space-y-1 mb-2">
          <li>
            <button
              onClick={() => onCategory('')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between gap-2 ${
                !activeCategory
                  ? 'bg-green-500 text-white font-semibold shadow-sm shadow-green-500/20'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <span>All Products</span>
              {!activeCategory && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </li>
        </ul>
        <div className="space-y-1">
          {visibleCatGroups.map(group => {
            const isGroupOpen = catGroupOpen === group.key
            // Check if active category is anywhere in this group (flat cats or nested subGroups)
            const allGroupCats = group.cats
              ? group.cats.map(c => c.value)
              : (group.subGroups || []).flatMap(sg => sg.cats.map(c => c.value))
            const hasActiveInGroup = allGroupCats.includes(activeCategory)

            return (
              <div key={group.key} className={`rounded-xl overflow-hidden border transition-colors ${
                hasActiveInGroup ? 'border-green-500/30 bg-green-500/5' : 'border-transparent'
              }`}>
                {/* Top-level group header */}
                <button
                  onClick={() => setCatGroupOpen(o => o === group.key ? '' : group.key)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-display font-bold flex justify-between items-center transition-colors ${
                    hasActiveInGroup
                      ? 'text-green-600 dark:text-green-400 bg-green-500/10'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="tracking-wide uppercase text-xs">{group.label}</span>
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                    isGroupOpen ? 'bg-primary text-white'
                    : hasActiveInGroup ? 'bg-green-500/20 text-green-500'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d={isGroupOpen ? 'M2 4.5L6 8.5L10 4.5' : 'M4.5 2L8.5 6L4.5 10'}
                        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>

                {isGroupOpen && (
                  <div className="px-2 pb-2">
                    {/* Group has sub-groups (e.g. Alcoholic → Beer / Wine / Spirits) */}
                    {group.subGroups ? (
                      <div className="space-y-0.5">
                        {group.subGroups.map(sg => {
                          const isSgOpen = subGroupOpen === sg.key
                          const hasActiveInSg = sg.cats.some(c => c.value === activeCategory)
                          return (
                            <div key={sg.key} className={`rounded-lg overflow-hidden ${
                              hasActiveInSg ? 'bg-green-500/5' : ''
                            }`}>
                              {/* Sub-group header */}
                              <button
                                onClick={() => setSubGroupOpen(o => o === sg.key ? '' : sg.key)}
                                className={`w-full text-left px-2.5 py-2 rounded-lg text-[13px] font-display font-semibold flex justify-between items-center transition-colors ${
                                  hasActiveInSg
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                              >
                                <span>{sg.label}</span>
                                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="flex-shrink-0 opacity-60">
                                  <path d={isSgOpen ? 'M2 4.5L6 8.5L10 4.5' : 'M4.5 2L8.5 6L4.5 10'}
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              {/* Sub-group items */}
                              {isSgOpen && (
                                <ul className="pl-2 pb-1 space-y-0.5">
                                  {sg.cats.map(cat => {
                                    const isActive = activeCategory === cat.value
                                    return (
                                      <li key={cat.value}>
                                        <button
                                          onClick={() => onCategory(cat.value)}
                                          className={`w-full text-left px-3 py-1.5 rounded-lg text-[13px] transition-all flex items-center justify-between gap-2 ${
                                            isActive
                                              ? 'bg-green-500 text-white font-semibold shadow-sm shadow-green-500/20'
                                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                          }`}
                                        >
                                          <span>{cat.label}</span>
                                          {isActive && (
                                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                                              <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                          )}
                                        </button>
                                      </li>
                                    )
                                  })}
                                </ul>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      /* Group has flat cats (Non-Alcoholic, Tobacco, Accessories) */
                      <ul className="space-y-0.5">
                        {(group.cats || []).map(cat => {
                          const isActive = activeCategory === cat.value
                          return (
                            <li key={cat.value}>
                              <button
                                onClick={() => onCategory(cat.value)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between gap-2 ${
                                  isActive
                                    ? 'bg-green-500 text-white font-semibold shadow-sm shadow-green-500/20'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                }`}
                              >
                                <span>{cat.label}</span>
                                {isActive && (
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                                    <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>



      <div>
        <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-3 text-sm uppercase tracking-wide">
          Price Range
        </h4>
        <ul className="space-y-1">
          {PRICE_RANGES.map(r => {
            const isAny    = r.min === null && r.max === null
            const isActive = isAny
              ? (!priceMin && !priceMax)
              : (priceMin == r.min && priceMax == r.max)
            return (
              <li key={r.label}>
                <button
                  onClick={() => isAny ? onClear() : onPriceRange(r.min, r.max)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-red-400 font-medium'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {r.label}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-red-400 text-xs font-display font-medium">
      {label}
      <button onClick={onRemove} className="hover:text-primary-dark">
        <X size={12} />
      </button>
    </span>
  )
}
